import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Building2, Tag as TagIcon, CheckCircle2, Newspaper, Users, MousePointerClick, Eye, Phone, Mail } from 'lucide-react';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import type { AsyncResource } from '../hooks/use-async';
import type { DashboardSnapshot, DashboardKpi } from '@application/use-cases/dashboard';
import type { Offer } from '@domain/entities';
import { ConfirmModal, Drawer, EmptyState, ErrorState, RefreshButton, StatusBadge, TableSkeleton, Tag } from '../components/ui';

const KPI_ICONS: Record<string, React.ElementType> = {
  pendingCompanies: Building2,
  pendingOffers: TagIcon,
  activeOffers: CheckCircle2,
  publishedArticles: Newspaper,
  activeUsers: Users,
};

function KpiIcon({ kpi }: { kpi: DashboardKpi }) {
  const Icon = KPI_ICONS[kpi.key];
  if (!Icon) return null;
  return (
    <div
      aria-hidden="true"
      style={{
        width: 40,
        height: 40,
        borderRadius: 12,
        background: `${kpi.color}22`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 14,
        flexShrink: 0,
      }}
    >
      <Icon size={20} color={kpi.color} strokeWidth={2} />
    </div>
  );
}

function OfferDetailDrawer({ offer, onClose }: { offer: Offer; onClose: () => void }) {
  return (
    <Drawer title={offer.title} subtitle={offer.company} onClose={onClose}>
      <div style={{ marginBottom: 10 }}>
        <StatusBadge status={offer.status} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        {[
          { Icon: MousePointerClick, label: 'Clics', value: offer.clicks },
          { Icon: Eye, label: 'Vues', value: offer.viewsCount },
          { Icon: Phone, label: 'Appels', value: offer.callClicksCount },
          { Icon: Mail, label: 'E-mails', value: offer.emailClicksCount },
        ].map(({ Icon, label, value }) => (
          <div key={label} style={{ background: '#F7F9FB', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon size={16} color="#8A95A3" strokeWidth={2} />
            <div>
              <div style={{ fontSize: 11, color: '#8A95A3', fontWeight: 700 }}>{label}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1E3347' }}>{value}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#8A95A3', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Catégorie</div>
        <div style={{ fontSize: 13.5 }}>{offer.category || '—'}</div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#8A95A3', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Formule</div>
        <div style={{ fontSize: 13.5 }}>{offer.pricing || '—'}</div>
      </div>
      {offer.description ? (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#8A95A3', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Description</div>
          <p style={{ fontSize: 13, color: '#4A5560', lineHeight: 1.65, margin: 0 }}>{offer.description}</p>
        </div>
      ) : null}
      {offer.photoUrls.length > 0 ? (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#8A95A3', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Photos</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {offer.photoUrls.map((url, i) => (
              <img key={i} src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10, border: '1px solid #DDE2E7' }} />
            ))}
          </div>
        </div>
      ) : null}
      {offer.publishedAt ? (
        <div style={{ fontSize: 12, color: '#8A95A3', marginTop: 8 }}>
          Publiée le {new Date(offer.publishedAt).toLocaleDateString('fr-FR')}
          {offer.expiresAt ? ` · expire le ${new Date(offer.expiresAt).toLocaleDateString('fr-FR')}` : ''}
        </div>
      ) : null}
    </Drawer>
  );
}

type ShellContext = { snapshot: AsyncResource<DashboardSnapshot>; refreshBadges: () => void };

export function DashboardPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { snapshot } = useOutletContext<ShellContext>();

  const [viewingOffer, setViewingOffer] = useState<Offer | null>(null);
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const moderate = useCallback(
    async (kind: 'company' | 'offer', id: string, approve: boolean) => {
      try {
        if (kind === 'company') {
          await container.companies.moderate.execute({
            ids: [id],
            status: approve ? 'approved' : 'rejected',
          });
          toast.notify(approve ? 'Compte approuvé' : 'Compte rejeté');
        } else {
          await container.offers.moderate.execute({
            ids: [id],
            status: approve ? 'active' : 'rejected',
          });
          toast.notify(
            approve ? 'Offre activée · notification envoyée' : 'Offre rejetée · notification envoyée',
          );
        }
        snapshot.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, snapshot],
  );

  if (snapshot.loading) return <TableSkeleton />;
  if (snapshot.error) {
    return (
      <ErrorState
        status={snapshot.error.status}
        message={snapshot.error.message}
        onRetry={snapshot.reload}
      />
    );
  }
  if (!snapshot.data) return <EmptyState title="Aucune donnée" body="Rien à afficher." />;

  const { kpis, pending: pendingItems, topOffers } = snapshot.data;

  return (
    <>
      <div className="bbm-toolbar" style={{ marginBottom: 12 }}>
        <div className="bbm-toolbar__start" />
        <div className="bbm-toolbar__end">
          <RefreshButton onRefresh={snapshot.reload} loading={snapshot.loading} />
        </div>
      </div>
      <div className="bbm-kpi-grid">
        {kpis.map((kpi) => (
          <div key={kpi.key} className="bbm-card">
            <KpiIcon kpi={kpi} />
            <div className="bbm-kpi__value">{kpi.value}</div>
            <div className="bbm-kpi__label">{kpi.label}</div>
          </div>
        ))}
      </div>

      <div className="bbm-two-col">
        <section className="bbm-card">
          <h2 className="bbm-h2">Demandes en attente</h2>
          {pendingItems.length === 0 ? (
            <div style={{ fontSize: 13.5, color: '#8A95A3', padding: '8px 0' }}>
              Aucune demande en attente.
            </div>
          ) : (
            pendingItems.map((item) => (
              <div
                key={`${item.kind}-${item.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '11px 0',
                  borderBottom: '1px solid #F0F2F5',
                  gap: 12,
                }}
              >
                <div>
                  <Tag
                    background={item.kind === 'company' ? '#E0A55222' : '#7FB3D522'}
                    color={item.kind === 'company' ? '#8A5B22' : '#2C5878'}
                  >
                    {item.typeLabel}
                  </Tag>
                  <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{item.name}</div>
                  <div style={{ fontSize: 12.5, color: '#8A95A3' }}>{item.subtitle}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    className="bbm-btn bbm-btn--success"
                    onClick={() => setPending({
                      title: `Approuver ${item.kind === 'company' ? 'ce compte' : 'cette offre'} ?`,
                      body: 'Le statut sera mis à jour immédiatement.',
                      confirmLabel: 'Approuver',
                      fn: () => void moderate(item.kind, item.id, true),
                    })}
                  >
                    Approuver
                  </button>
                  <button
                    type="button"
                    className="bbm-btn bbm-btn--danger"
                    onClick={() => setPending({
                      title: `Rejeter ${item.kind === 'company' ? 'ce compte' : 'cette offre'} ?`,
                      body: 'Le statut sera mis à jour immédiatement.',
                      confirmLabel: 'Rejeter',
                      danger: true,
                      fn: () => void moderate(item.kind, item.id, false),
                    })}
                  >
                    Rejeter
                  </button>
                </div>
              </div>
            ))
          )}
        </section>

        <section className="bbm-card">
          <h2 className="bbm-h2">Offres les plus cliquées</h2>
          {topOffers.map((offer) => (
            <button
              key={offer.id}
              type="button"
              onClick={() => setViewingOffer(offer)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 0',
                borderBottom: '1px solid #F0F2F5',
                gap: 12,
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="bbm-truncate" style={{ fontSize: 13.5, fontWeight: 600 }}>
                  {offer.title}
                </div>
                <div style={{ fontSize: 12, color: '#8A95A3', marginTop: 2 }}>{offer.company}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <MousePointerClick size={13} color="#8A95A3" strokeWidth={2} />
                <span style={{ fontSize: 13.5, fontWeight: 800, color: '#33506B' }}>{offer.clicks}</span>
              </div>
            </button>
          ))}
        </section>
      </div>
      {viewingOffer ? (
        <OfferDetailDrawer offer={viewingOffer} onClose={() => setViewingOffer(null)} />
      ) : null}
      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </>
  );
}
